import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Store, Users, CreditCard, LogOut, Check, X, Trash2, 
  ChevronDown, ChevronUp, BarChart3, Mail, Bell, AlertTriangle, Crown,
  FileText, Plus, Pencil, Eye, EyeOff, HandHelping, Target, Camera,
  Zap, Download, DollarSign, TrendingUp
} from "lucide-react";
import { AdminRevenue } from "../components/admin/AdminRevenue";
import { AdminFounding } from "../components/admin/AdminFounding";
import axios from "axios";
import { toast } from "sonner";

import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { BlogPostEditor } from "../components/BlogEditor";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("businesses");
  const [businesses, setBusinesses] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [communitySubmissions, setCommunitySubmissions] = useState([]);
  const [mpListings, setMpListings] = useState([]);
  const [mpOpportunities, setMpOpportunities] = useState([]);
  const [mpConstruction, setMpConstruction] = useState([]);
  const [mpSignals, setMpSignals] = useState([]);
  const [foundingMembers, setFoundingMembers] = useState([]);
  const [revenueStats, setRevenueStats] = useState(null);
  const [commerceTransactions, setCommerceTransactions] = useState([]);
  const [showBlogEditor, setShowBlogEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedBusiness, setExpandedBusiness] = useState(null);

  useEffect(() => {
    const token = sessionStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    fetchData();
  }, [navigate]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${sessionStorage.getItem("adminToken")}` }
  });

  const fetchData = async () => {
    try {
      const [bizRes, subRes, transRes, statsRes, notifRes, memberRes, blogRes, volRes, oppRes, commRes, mpListRes, mpOppRes, mpConstRes, mpSigRes, foundingRes] = await Promise.all([
        axios.get(`${API}/admin/businesses`, getAuthHeaders()),
        axios.get(`${API}/admin/subscribers`, getAuthHeaders()),
        axios.get(`${API}/admin/transactions`, getAuthHeaders()),
        axios.get(`${API}/admin/stats`, getAuthHeaders()),
        axios.get(`${API}/admin/notifications`, getAuthHeaders()),
        axios.get(`${API}/admin/memberships`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/blog/posts`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/volunteers`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/opportunities`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/community-submissions`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/marketplace/listings`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/marketplace/opportunities`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/marketplace/construction`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/marketplace/signals`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/founding-members`, getAuthHeaders()).catch(() => ({ data: [] }))
      ]);
      setBusinesses(bizRes.data);
      setSubscribers(subRes.data);
      setTransactions(transRes.data);
      setStats(statsRes.data);
      setNotifications(notifRes.data);
      setMemberships(memberRes.data);
      setBlogPosts(blogRes.data);
      setVolunteers(volRes.data);
      setOpportunities(oppRes.data);
      setCommunitySubmissions(commRes.data);
      setMpListings(mpListRes.data);
      setMpOpportunities(mpOppRes.data);
      setMpConstruction(mpConstRes.data);
      setMpSignals(mpSigRes.data);
      setFoundingMembers(foundingRes.data);

      // Fetch revenue data separately (may fail for non-user-based admin)
      try {
        const [revRes, comTxnRes] = await Promise.all([
          axios.get(`${API}/commerce/admin/stats`, getAuthHeaders()),
          axios.get(`${API}/commerce/admin/transactions`, getAuthHeaders()),
        ]);
        setRevenueStats(revRes.data);
        setCommerceTransactions(comTxnRes.data);
      } catch { /* commerce endpoints may need user-auth, skip */ }
      setLoading(false);
    } catch (e) {
      console.error("Error fetching admin data:", e);
      if (e.response?.status === 401) {
        sessionStorage.removeItem("adminToken");
        navigate("/admin/login");
      }
      setLoading(false);
    }
  };

  const handleApprove = async (businessId, approved) => {
    try {
      await axios.put(`${API}/admin/businesses/${businessId}`, 
        { is_approved: approved }, 
        getAuthHeaders()
      );
      toast.success(approved ? "Business approved" : "Business unapproved");
      fetchData();
    } catch (e) {
      toast.error("Failed to update business");
    }
  };

  const handleFeature = async (businessId, featured) => {
    try {
      await axios.put(`${API}/admin/businesses/${businessId}`, 
        { is_featured: featured }, 
        getAuthHeaders()
      );
      toast.success(featured ? "Business featured" : "Business unfeatured");
      fetchData();
    } catch (e) {
      toast.error("Failed to update business");
    }
  };

  const handleSpotlight = async (businessId) => {
    try {
      await axios.put(`${API}/admin/businesses/${businessId}`, 
        { is_spotlight: true }, 
        getAuthHeaders()
      );
      toast.success("Business set as spotlight!");
      fetchData();
    } catch (e) {
      toast.error("Failed to update spotlight");
    }
  };

  const handleDelete = async (businessId) => {
    if (!window.confirm("Are you sure you want to delete this business?")) return;
    try {
      await axios.delete(`${API}/admin/businesses/${businessId}`, getAuthHeaders());
      toast.success("Business deleted");
      fetchData();
    } catch (e) {
      toast.error("Failed to delete business");
    }
  };

  const handleTierChange = async (businessId, tier) => {
    try {
      await axios.put(`${API}/admin/businesses/${businessId}`, 
        { membership_tier: tier }, 
        getAuthHeaders()
      );
      toast.success("Membership tier updated");
      fetchData();
    } catch (e) {
      toast.error("Failed to update tier");
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("adminToken");
    sessionStorage.removeItem("adminEmail");
    navigate("/admin/login");
  };

  const handleClearTestData = async () => {
    if (!window.confirm("Are you sure you want to clear ALL test data? This will remove all businesses and start fresh.")) return;
    try {
      await axios.post(`${API}/admin/clear-test-data`, {}, getAuthHeaders());
      toast.success("Test data cleared. Ready for real listings.");
      fetchData();
    } catch (e) {
      toast.error("Failed to clear test data");
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm("Delete this blog post?")) return;
    try {
      await axios.delete(`${API}/admin/blog/posts/${postId}`, getAuthHeaders());
      toast.success("Post deleted");
      fetchData();
    } catch (e) {
      toast.error("Failed to delete post");
    }
  };

  const handleTogglePublish = async (post) => {
    try {
      await axios.put(`${API}/admin/blog/posts/${post.id}`, 
        { is_published: !post.is_published }, 
        getAuthHeaders()
      );
      toast.success(post.is_published ? "Post unpublished" : "Post published");
      fetchData();
    } catch (e) {
      toast.error("Failed to update post");
    }
  };

  const getToken = () => sessionStorage.getItem("adminToken");

  const handleVolunteerStatus = async (volId, status) => {
    try {
      await axios.put(`${API}/admin/volunteers/${volId}`, { status }, getAuthHeaders());
      toast.success(`Volunteer ${status}`);
      fetchData();
    } catch (e) {
      toast.error("Failed to update volunteer");
    }
  };

  const handleVolunteerBadge = async (volId, badge_level) => {
    try {
      await axios.put(`${API}/admin/volunteers/${volId}`, { badge_level }, getAuthHeaders());
      toast.success(`Badge updated to ${badge_level}`);
      fetchData();
    } catch (e) {
      toast.error("Failed to update badge");
    }
  };

  const handleDeleteVolunteer = async (volId) => {
    if (!window.confirm("Remove this volunteer?")) return;
    try {
      await axios.delete(`${API}/admin/volunteers/${volId}`, getAuthHeaders());
      toast.success("Volunteer removed");
      fetchData();
    } catch (e) {
      toast.error("Failed to remove volunteer");
    }
  };

  const handleOppStatus = async (oppId, updates) => {
    try {
      await axios.put(`${API}/admin/opportunities/${oppId}`, updates, getAuthHeaders());
      toast.success("Opportunity updated");
      fetchData();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const handleDeleteOpp = async (oppId) => {
    if (!window.confirm("Delete this opportunity?")) return;
    try {
      await axios.delete(`${API}/admin/opportunities/${oppId}`, getAuthHeaders());
      toast.success("Opportunity deleted");
      fetchData();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#EFEDE5] flex items-center justify-center">
        <div className="text-[#5C5A52]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#EFEDE5]">
      {/* Header */}
      <header className="bg-[#1A2F23] text-white px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-heading text-xl font-bold">ekvalleys.com Admin</h1>
            <p className="text-white/60 text-sm">{sessionStorage.getItem("adminEmail")}</p>
          </div>
          <div className="flex items-center gap-4">
            {stats.unread_notifications > 0 && (
              <div className="flex items-center gap-2 bg-[#D4A017] text-[#1A2F23] px-3 py-1 rounded-full text-sm font-medium">
                <Bell className="w-4 h-4" />
                {stats.unread_notifications} new
              </div>
            )}
            <Button 
              variant="ghost" 
              className="text-white/80 hover:text-white hover:bg-white/10"
              onClick={handleLogout}
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Stats */}
      <section className="px-6 py-6 border-b-2 border-[#D1CFC5]">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-[#F7F6F2] border-2 border-[#D1CFC5] rounded-lg p-4">
            <div className="flex items-center gap-2 text-[#5C5A52] mb-1">
              <Store className="w-4 h-4" />
              <span className="text-xs">Total</span>
            </div>
            <p className="text-2xl font-bold text-[#1A1915]" data-testid="stat-total-businesses">
              {stats.total_businesses}
            </p>
          </div>
          <div className="bg-[#F7F6F2] border-2 border-[#D1CFC5] rounded-lg p-4">
            <div className="flex items-center gap-2 text-[#5C5A52] mb-1">
              <Check className="w-4 h-4" />
              <span className="text-xs">Approved</span>
            </div>
            <p className="text-2xl font-bold text-[#2C4A3B]" data-testid="stat-approved">
              {stats.approved_businesses}
            </p>
          </div>
          <div className="bg-[#F7F6F2] border-2 border-[#D1CFC5] rounded-lg p-4">
            <div className="flex items-center gap-2 text-[#5C5A52] mb-1">
              <BarChart3 className="w-4 h-4" />
              <span className="text-xs">Pending</span>
            </div>
            <p className="text-2xl font-bold text-[#B85C38]" data-testid="stat-pending">
              {stats.pending_businesses}
            </p>
          </div>
          <div className="bg-[#F7F6F2] border-2 border-[#D1CFC5] rounded-lg p-4">
            <div className="flex items-center gap-2 text-[#5C5A52] mb-1">
              <CreditCard className="w-4 h-4" />
              <span className="text-xs">Founding</span>
            </div>
            <p className="text-2xl font-bold text-[#D4A017]" data-testid="stat-founding">
              {stats.founding_members}
            </p>
          </div>
          <div className="bg-[#F7F6F2] border-2 border-[#D1CFC5] rounded-lg p-4">
            <div className="flex items-center gap-2 text-[#5C5A52] mb-1">
              <Mail className="w-4 h-4" />
              <span className="text-xs">Subscribers</span>
            </div>
            <p className="text-2xl font-bold text-[#1A1915]" data-testid="stat-subscribers">
              {stats.subscribers}
            </p>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="px-6 py-4 bg-[#F7F6F2] border-b-2 border-[#D1CFC5]">
        <div className="max-w-7xl mx-auto flex flex-wrap gap-4 items-center justify-between">
          <div className="flex gap-4">
            <Button
              variant={activeTab === "businesses" ? "default" : "ghost"}
              className={activeTab === "businesses" ? "bg-[#2C4A3B]" : ""}
              onClick={() => setActiveTab("businesses")}
              data-testid="tab-businesses"
            >
              <Store className="w-4 h-4 mr-2" />
              Businesses
            </Button>
            <Button
              variant={activeTab === "notifications" ? "default" : "ghost"}
              className={activeTab === "notifications" ? "bg-[#2C4A3B]" : ""}
              onClick={() => setActiveTab("notifications")}
              data-testid="tab-notifications"
            >
              <Bell className="w-4 h-4 mr-2" />
              Submissions
              {notifications.filter(n => !n.read).length > 0 && (
                <span className="ml-2 bg-[#D4A017] text-[#1A2F23] text-xs px-2 py-0.5 rounded-full">
                  {notifications.filter(n => !n.read).length}
                </span>
              )}
            </Button>
            <Button
              variant={activeTab === "subscribers" ? "default" : "ghost"}
              className={activeTab === "subscribers" ? "bg-[#2C4A3B]" : ""}
              onClick={() => setActiveTab("subscribers")}
              data-testid="tab-subscribers"
            >
              <Users className="w-4 h-4 mr-2" />
              Subscribers
            </Button>
            <Button
              variant={activeTab === "transactions" ? "default" : "ghost"}
              className={activeTab === "transactions" ? "bg-[#2C4A3B]" : ""}
              onClick={() => setActiveTab("transactions")}
              data-testid="tab-transactions"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Transactions
            </Button>
            <Button
              variant={activeTab === "memberships" ? "default" : "ghost"}
              className={activeTab === "memberships" ? "bg-[#2C4A3B]" : ""}
              onClick={() => setActiveTab("memberships")}
              data-testid="tab-memberships"
            >
              <Crown className="w-4 h-4 mr-2" />
              Registrations
            </Button>
            <Button
              variant={activeTab === "blog" ? "default" : "ghost"}
              className={activeTab === "blog" ? "bg-[#2C4A3B]" : ""}
              onClick={() => setActiveTab("blog")}
              data-testid="tab-blog"
            >
              <FileText className="w-4 h-4 mr-2" />
              Blog
            </Button>
            <Button
              variant={activeTab === "volunteers" ? "default" : "ghost"}
              className={activeTab === "volunteers" ? "bg-[#2C4A3B]" : ""}
              onClick={() => setActiveTab("volunteers")}
              data-testid="tab-volunteers"
            >
              <HandHelping className="w-4 h-4 mr-2" />
              Volunteers ({volunteers.length})
            </Button>
            <Button
              variant={activeTab === "opportunities" ? "default" : "ghost"}
              className={activeTab === "opportunities" ? "bg-[#2C4A3B]" : ""}
              onClick={() => setActiveTab("opportunities")}
              data-testid="tab-opportunities"
            >
              <Target className="w-4 h-4 mr-2" />
              Opportunities ({opportunities.length})
            </Button>
            <Button
              variant={activeTab === "community" ? "default" : "ghost"}
              className={activeTab === "community" ? "bg-[#E87040]" : ""}
              onClick={() => setActiveTab("community")}
              data-testid="tab-community"
            >
              <Camera className="w-4 h-4 mr-2" />
              Moments ({communitySubmissions.filter(s => s.status === "pending").length})
            </Button>
            <Button
              variant={activeTab === "marketplace" ? "default" : "ghost"}
              className={activeTab === "marketplace" ? "bg-[#D4A017] text-[#1A1915]" : ""}
              onClick={() => setActiveTab("marketplace")}
              data-testid="tab-marketplace"
            >
              <Store className="w-4 h-4 mr-2" />
              Marketplace ({mpListings.filter(l => !l.is_approved).length + mpOpportunities.filter(o => !o.is_approved).length + mpConstruction.filter(c => !c.is_approved).length + mpSignals.filter(s => !s.is_approved).length})
            </Button>
            <Button
              variant={activeTab === "founding" ? "default" : "ghost"}
              className={activeTab === "founding" ? "bg-[#14B8A6]" : ""}
              onClick={() => setActiveTab("founding")}
              data-testid="tab-founding"
            >
              <Zap className="w-4 h-4 mr-2" />
              Founding ({foundingMembers.length})
            </Button>
            <Button
              variant={activeTab === "revenue" ? "default" : "ghost"}
              className={activeTab === "revenue" ? "bg-[#D4A017] text-[#1A1915]" : ""}
              onClick={() => setActiveTab("revenue")}
              data-testid="tab-revenue"
            >
              <DollarSign className="w-4 h-4 mr-2" />
              Revenue
            </Button>
          </div>
          <Button
            variant="outline"
            className="text-[#B85C38] border-[#B85C38] hover:bg-[#B85C38] hover:text-white"
            onClick={handleClearTestData}
            data-testid="clear-test-data-btn"
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Clear Test Data
          </Button>
        </div>
      </section>

      {/* Content */}
      <section className="px-6 py-6">
        <div className="max-w-7xl mx-auto">
          {/* Businesses Tab */}
          {activeTab === "businesses" && (
            <div className="bg-white border-2 border-[#D1CFC5] rounded-xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Business</TableHead>
                    <TableHead>Town</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {businesses.map((biz) => (
                    <>
                      <TableRow 
                        key={biz.id} 
                        className="cursor-pointer hover:bg-[#F7F6F2]"
                        onClick={() => setExpandedBusiness(expandedBusiness === biz.id ? null : biz.id)}
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {expandedBusiness === biz.id ? (
                              <ChevronUp className="w-4 h-4 text-[#5C5A52]" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-[#5C5A52]" />
                            )}
                            <div>
                              <p className="font-medium text-[#1A1915]">{biz.name}</p>
                              {biz.email && (
                                <p className="text-xs text-[#5C5A52]">{biz.email}</p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{biz.town}</TableCell>
                        <TableCell>{biz.category}</TableCell>
                        <TableCell>
                          <Select 
                            value={biz.membership_tier} 
                            onValueChange={(v) => handleTierChange(biz.id, v)}
                          >
                            <SelectTrigger className="w-28 h-8" onClick={(e) => e.stopPropagation()}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="standard">Standard</SelectItem>
                              <SelectItem value="founding">Founding</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {biz.is_approved ? (
                              <Badge className="bg-[#2C4A3B]">Approved</Badge>
                            ) : (
                              <Badge className="bg-[#B85C38]">Pending</Badge>
                            )}
                            {biz.is_featured && (
                              <Badge className="bg-[#D4A017] text-[#1A1915]">Featured</Badge>
                            )}
                            {biz.is_spotlight && (
                              <Badge className="bg-[#7C3AED] text-white">Spotlight</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <Button
                              size="sm"
                              variant={biz.is_approved ? "outline" : "default"}
                              className={!biz.is_approved ? "bg-[#2C4A3B]" : ""}
                              onClick={() => handleApprove(biz.id, !biz.is_approved)}
                            >
                              {biz.is_approved ? <X className="w-3 h-3" /> : <Check className="w-3 h-3" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className={biz.is_featured ? "border-[#D4A017] text-[#D4A017]" : ""}
                              onClick={() => handleFeature(biz.id, !biz.is_featured)}
                              title="Toggle Featured"
                            >
                              ★
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className={biz.is_spotlight ? "border-[#7C3AED] text-[#7C3AED]" : ""}
                              onClick={() => handleSpotlight(biz.id)}
                              title="Set as Spotlight"
                              data-testid={`spotlight-btn-${biz.id}`}
                            >
                              <Eye className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => handleDelete(biz.id)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      {expandedBusiness === biz.id && (
                        <TableRow>
                          <TableCell colSpan={6} className="bg-[#F7F6F2]">
                            <div className="p-4 grid md:grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-[#5C5A52]">Description:</p>
                                <p className="text-[#1A1915]">{biz.description}</p>
                              </div>
                              <div className="space-y-2">
                                {biz.owner_name && <p><span className="text-[#5C5A52]">Owner:</span> {biz.owner_name}</p>}
                                {biz.phone && <p><span className="text-[#5C5A52]">Phone:</span> {biz.phone}</p>}
                                {biz.website && <p><span className="text-[#5C5A52]">Website:</span> {biz.website}</p>}
                                {biz.hours && <p><span className="text-[#5C5A52]">Hours:</span> {biz.hours}</p>}
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  ))}
                </TableBody>
              </Table>
              {businesses.length === 0 && (
                <div className="p-8 text-center text-[#5C5A52]">
                  No businesses yet.
                </div>
              )}
            </div>
          )}

          {/* Subscribers Tab */}
          {activeTab === "subscribers" && (
            <div className="bg-white border-2 border-[#D1CFC5] rounded-xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Subscribed At</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subscribers.map((sub) => (
                    <TableRow key={sub.id}>
                      <TableCell className="font-medium">{sub.email}</TableCell>
                      <TableCell>
                        {new Date(sub.subscribed_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {subscribers.length === 0 && (
                <div className="p-8 text-center text-[#5C5A52]">
                  No subscribers yet.
                </div>
              )}
            </div>
          )}

          {/* Notifications/Submissions Tab */}
          {activeTab === "notifications" && (
            <div className="space-y-4">
              <div className="bg-[#F7F6F2] border-2 border-[#D1CFC5] rounded-xl p-4">
                <p className="text-[#5C5A52] text-sm">
                  <strong>Email notifications:</strong> All new submissions are logged here. 
                  Configure email forwarding to <strong>intuitdevelopmentgroup@gmail.com</strong> in your email service.
                </p>
              </div>
              {notifications.length > 0 ? (
                <div className="space-y-3">
                  {notifications.map((notif) => (
                    <div 
                      key={notif.id} 
                      className={`bg-white border-2 rounded-xl p-4 ${notif.read ? 'border-[#D1CFC5]' : 'border-[#D4A017]'}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            {!notif.read && <span className="w-2 h-2 bg-[#D4A017] rounded-full" />}
                            <h3 className="font-semibold text-[#1A1915]">{notif.business_name}</h3>
                            <Badge className={notif.membership_tier === "founding" ? "bg-[#D4A017] text-[#1A1915]" : "bg-[#E5E2D9] text-[#1A1915]"}>
                              {notif.membership_tier}
                            </Badge>
                          </div>
                          <div className="text-sm text-[#5C5A52] space-y-1">
                            <p><strong>Email:</strong> {notif.email}</p>
                            {notif.phone && <p><strong>Phone:</strong> {notif.phone}</p>}
                            <p><strong>Location:</strong> {notif.town} • {notif.category}</p>
                            <p className="text-xs text-[#5C5A52]/60">
                              {new Date(notif.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-white border-2 border-[#D1CFC5] rounded-xl p-8 text-center text-[#5C5A52]">
                  No submissions yet. New business submissions will appear here.
                </div>
              )}
            </div>
          )}

          {/* Transactions Tab */}
          {activeTab === "transactions" && (
            <div className="bg-white border-2 border-[#D1CFC5] rounded-xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((trans) => (
                    <TableRow key={trans.id}>
                      <TableCell className="font-medium">{trans.email}</TableCell>
                      <TableCell>${trans.amount} {trans.currency.toUpperCase()}</TableCell>
                      <TableCell>
                        <Badge 
                          className={
                            trans.payment_status === "paid" ? "bg-[#2C4A3B]" :
                            trans.payment_status === "pending" ? "bg-[#D4A017] text-[#1A1915]" :
                            "bg-[#B85C38]"
                          }
                        >
                          {trans.payment_status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {new Date(trans.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {transactions.length === 0 && (
                <div className="p-8 text-center text-[#5C5A52]">
                  No transactions yet.
                </div>
              )}
            </div>
          )}

          {/* Memberships Tab */}
          {activeTab === "memberships" && (
            <div className="bg-white border-2 border-[#D1CFC5] rounded-xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead>Expires</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {memberships.map((mem) => (
                    <TableRow key={mem.id}>
                      <TableCell className="font-medium">{mem.user_email}</TableCell>
                      <TableCell>{mem.user_name || "—"}</TableCell>
                      <TableCell>
                        <Badge className={
                          mem.tier_name === "founding" ? "bg-[#14B8A6] text-[#1A1915]" :
                          mem.tier_name === "elite" ? "bg-[#1A2F23] text-[#D4A017]" :
                          mem.tier_name === "premium" ? "bg-[#D4A017] text-[#1A1915]" :
                          "bg-[#2C4A3B]"
                        }>
                          {mem.tier_name}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          mem.status === "active" ? "bg-[#2C4A3B]" :
                          mem.status === "pending" ? "bg-[#D4A017] text-[#1A1915]" :
                          "bg-[#B85C38]"
                        }>
                          {mem.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {mem.started_at ? new Date(mem.started_at).toLocaleDateString() : "—"}
                      </TableCell>
                      <TableCell>
                        {mem.expires_at ? new Date(mem.expires_at).toLocaleDateString() : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {memberships.length === 0 && (
                <div className="p-8 text-center text-[#5C5A52]">
                  No memberships yet. When users subscribe through the membership system, they will appear here.
                </div>
              )}
            </div>
          )}

          {/* Blog Tab */}
          {activeTab === "blog" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-xl font-semibold text-[#1A1915]">
                  Blog Posts ({blogPosts.length})
                </h2>
                <Button
                  onClick={() => { setEditingPost(null); setShowBlogEditor(true); }}
                  className="bg-[#2C4A3B] hover:bg-[#1A2F23] text-white"
                  data-testid="create-post-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Post
                </Button>
              </div>

              {blogPosts.length > 0 ? (
                <div className="bg-white border-2 border-[#D1CFC5] rounded-xl overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Author</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {blogPosts.map((post) => (
                        <TableRow key={post.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-[#1A1915]">{post.title}</p>
                              <p className="text-xs text-[#5C5A52] truncate max-w-[250px]">{post.excerpt}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              post.category === "sponsor" ? "bg-[#D4A017] text-[#1A2F23]" :
                              post.category === "premium" ? "bg-[#2C4A3B]" :
                              post.category === "news" ? "bg-[#3B82F6]" :
                              "bg-[#B85C38]"
                            }>
                              {post.category}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-[#5C5A52]">{post.author}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Badge className={post.is_published ? "bg-[#2C4A3B]" : "bg-[#5C5A52]"}>
                                {post.is_published ? "Published" : "Draft"}
                              </Badge>
                              {post.is_featured && (
                                <Badge className="bg-[#D4A017] text-[#1A1915]">Featured</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-[#5C5A52] text-sm">
                            {new Date(post.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => { setEditingPost(post); setShowBlogEditor(true); }}
                                title="Edit"
                              >
                                <Pencil className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleTogglePublish(post)}
                                title={post.is_published ? "Unpublish" : "Publish"}
                              >
                                {post.is_published ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-500 hover:text-red-700"
                                onClick={() => handleDeletePost(post.id)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="bg-white border-2 border-[#D1CFC5] rounded-xl p-8 text-center text-[#5C5A52]">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-[#D1CFC5]" />
                  <p className="mb-2">No blog posts yet.</p>
                  <p className="text-sm">Click "New Post" to create your first article.</p>
                </div>
              )}
            </div>
          )}

          {/* Volunteers Tab */}
          {activeTab === "volunteers" && (
            <div className="space-y-4">
              <h2 className="font-heading text-xl font-semibold text-[#1A1915]">
                Volunteers ({volunteers.length})
              </h2>

              {volunteers.length > 0 ? (
                <div className="bg-white border-2 border-[#D1CFC5] rounded-xl overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Town</TableHead>
                        <TableHead>Skills</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Badge</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {volunteers.map((vol) => (
                        <TableRow key={vol.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-[#1A1915]">{vol.first_name} {vol.last_name}</p>
                              {vol.phone && <p className="text-xs text-[#5C5A52]">{vol.phone}</p>}
                            </div>
                          </TableCell>
                          <TableCell className="text-[#5C5A52] text-sm">{vol.email}</TableCell>
                          <TableCell className="text-[#5C5A52]">{vol.town}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1 max-w-[200px]">
                              {(vol.talents || []).slice(0, 2).map((t) => (
                                <Badge key={t} className="bg-[#2C4A3B]/10 text-[#2C4A3B] text-[10px]">{t}</Badge>
                              ))}
                              {(vol.talents || []).length > 2 && (
                                <Badge className="bg-[#5C5A52]/10 text-[#5C5A52] text-[10px]">+{vol.talents.length - 2}</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <select
                              value={vol.status}
                              onChange={(e) => handleVolunteerStatus(vol.id, e.target.value)}
                              className="text-xs border border-[#D1CFC5] rounded px-2 py-1 bg-white"
                            >
                              <option value="pending">Pending</option>
                              <option value="approved">Approved</option>
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                            </select>
                          </TableCell>
                          <TableCell>
                            <select
                              value={vol.badge_level || "silver"}
                              onChange={(e) => handleVolunteerBadge(vol.id, e.target.value)}
                              className="text-xs border border-[#D1CFC5] rounded px-2 py-1 bg-white"
                            >
                              <option value="silver">Silver</option>
                              <option value="gold">Gold</option>
                            </select>
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => handleDeleteVolunteer(vol.id)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="bg-white border-2 border-[#D1CFC5] rounded-xl p-8 text-center text-[#5C5A52]">
                  <HandHelping className="w-12 h-12 mx-auto mb-3 text-[#D1CFC5]" />
                  <p className="mb-2">No volunteers yet.</p>
                  <p className="text-sm">Volunteers who register through the Volunteer page will appear here.</p>
                </div>
              )}
            </div>
          )}

          {/* Opportunities Tab */}
          {activeTab === "opportunities" && (
            <div className="space-y-4">
              <h2 className="font-heading text-xl font-semibold text-[#1A1915]">
                Opportunities & Challenges ({opportunities.length})
              </h2>

              {opportunities.length > 0 ? (
                <div className="bg-white border-2 border-[#D1CFC5] rounded-xl overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Posted By</TableHead>
                        <TableHead>Helpers</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Approved</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {opportunities.map((opp) => (
                        <TableRow key={opp.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-[#1A1915]">{opp.title}</p>
                              <p className="text-xs text-[#5C5A52] truncate max-w-[200px]">{opp.description}</p>
                              {opp.suggested_category && (
                                <p className="text-xs text-[#D4A017] mt-0.5">Suggested: {opp.suggested_category}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell><Badge className="bg-[#F7F6F2] text-[#5C5A52] text-xs">{opp.category}</Badge></TableCell>
                          <TableCell className="text-sm text-[#5C5A52]">{opp.poster_name || opp.poster_email}</TableCell>
                          <TableCell className="text-sm">{(opp.helpers || []).length}</TableCell>
                          <TableCell>
                            <select
                              value={opp.status}
                              onChange={(e) => handleOppStatus(opp.id, { status: e.target.value })}
                              className="text-xs border border-[#D1CFC5] rounded px-2 py-1 bg-white"
                            >
                              <option value="open">Open</option>
                              <option value="in_progress">In Progress</option>
                              <option value="completed">Completed</option>
                            </select>
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant={opp.is_approved ? "default" : "outline"}
                              className={opp.is_approved ? "bg-[#2C4A3B] text-white text-xs" : "text-xs"}
                              onClick={() => handleOppStatus(opp.id, { is_approved: !opp.is_approved })}
                            >
                              {opp.is_approved ? "Approved" : "Approve"}
                            </Button>
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => handleDeleteOpp(opp.id)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="bg-white border-2 border-[#D1CFC5] rounded-xl p-8 text-center text-[#5C5A52]">
                  <Target className="w-12 h-12 mx-auto mb-3 text-[#D1CFC5]" />
                  <p className="mb-2">No opportunities posted yet.</p>
                  <p className="text-sm">When community members post opportunities, they will appear here for approval.</p>
                </div>
              )}
            </div>
          )}

          {/* Community Moments Tab */}
          {activeTab === "community" && (
            <div className="bg-white border-2 border-[#D1CFC5] rounded-xl overflow-hidden">
              <div className="p-4 border-b border-[#D1CFC5] flex justify-between items-center">
                <h3 className="font-heading text-lg font-semibold text-[#1A1915]">
                  Community Submissions ({communitySubmissions.length})
                </h3>
                <div className="flex gap-2 text-sm">
                  <span className="text-[#D4A017] font-medium">{communitySubmissions.filter(s => s.status === "pending").length} pending</span>
                  <span className="text-[#14B8A6] font-medium">{communitySubmissions.filter(s => s.status === "approved").length} approved</span>
                </div>
              </div>
              {communitySubmissions.length > 0 ? (
                <div className="divide-y divide-[#D1CFC5]">
                  {communitySubmissions.map((sub) => (
                    <div key={sub.id} className="p-4 flex gap-4 items-start" data-testid={`community-sub-${sub.id}`}>
                      {sub.media_url && (
                        <img src={sub.media_url} alt="" className="w-20 h-20 object-cover rounded-lg flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-[#1A1915] font-medium">{sub.caption}</p>
                        <div className="flex items-center gap-3 mt-1 text-sm text-[#5C5A52]">
                          <span>{sub.name || "Anonymous"}</span>
                          {sub.town && <span>{sub.town}</span>}
                          <span>{new Date(sub.created_at).toLocaleDateString()}</span>
                        </div>
                        <Badge className={`mt-2 ${sub.status === "pending" ? "bg-[#D4A017]" : sub.status === "approved" ? "bg-[#14B8A6]" : "bg-red-500"} text-white`}>
                          {sub.status}
                        </Badge>
                      </div>
                      {sub.status === "pending" && (
                        <div className="flex gap-2 flex-shrink-0">
                          <Button size="sm" className="bg-[#14B8A6] hover:bg-[#0EA391] text-white"
                            onClick={async () => {
                              try {
                                await axios.put(`${API}/admin/community-submissions/${sub.id}/approve`, {}, getAuthHeaders());
                                toast.success("Approved!");
                                fetchData();
                              } catch { toast.error("Failed"); }
                            }}
                            data-testid={`approve-${sub.id}`}>
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" className="border-red-400 text-red-500 hover:bg-red-50"
                            onClick={async () => {
                              try {
                                await axios.put(`${API}/admin/community-submissions/${sub.id}/reject`, {}, getAuthHeaders());
                                toast.success("Rejected");
                                fetchData();
                              } catch { toast.error("Failed"); }
                            }}
                            data-testid={`reject-${sub.id}`}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-[#5C5A52]">
                  <Camera className="w-12 h-12 mx-auto mb-3 text-[#D1CFC5]" />
                  <p className="mb-2">No community submissions yet.</p>
                  <p className="text-sm">When community members share moments, they'll appear here for review.</p>
                </div>
              )}
            </div>
          )}

          {/* MARKETPLACE TAB */}
          {activeTab === "marketplace" && (
            <div className="space-y-6">
              <h2 className="font-heading text-2xl font-semibold text-[#1A1915]">Marketplace Moderation</h2>

              {/* Marketplace Listings */}
              <div className="bg-white border-2 border-[#D1CFC5] rounded-xl p-4">
                <h3 className="font-semibold text-[#1A1915] mb-3">Service Listings ({mpListings.length})</h3>
                {mpListings.length > 0 ? (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {mpListings.map(l => (
                      <div key={l.id} className={`flex items-center justify-between p-3 rounded-lg border ${l.is_approved ? "border-[#D1CFC5]" : "border-[#D4A017] bg-[#D4A017]/5"}`} data-testid={`mp-listing-${l.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-[#1A1915] truncate">{l.name} <span className="text-xs text-[#5C5A52]">({l.category})</span></p>
                          <p className="text-xs text-[#5C5A52]">{l.town} | {l.contact_email || "No email"}</p>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                          {!l.is_approved && <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-[#2C4A3B]" onClick={async () => { try { await axios.put(`${API}/admin/marketplace/listings/${l.id}/approve`, {}, getAuthHeaders()); toast.success("Approved"); fetchData(); } catch { toast.error("Failed"); }}}><Check className="w-4 h-4" /></Button>}
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-[#D4A017]" onClick={async () => { try { await axios.put(`${API}/admin/marketplace/listings/${l.id}/feature`, {}, getAuthHeaders()); toast.success("Toggled feature"); fetchData(); } catch { toast.error("Failed"); }}}><Crown className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-[#D4A017]" onClick={async () => { try { await axios.put(`${API}/admin/marketplace/listings/${l.id}/founding`, {}, getAuthHeaders()); toast.success("Toggled founding"); fetchData(); } catch { toast.error("Failed"); }}} title="Toggle Founding Partner"><Target className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-[#B85C38]" onClick={async () => { try { await axios.delete(`${API}/admin/marketplace/listings/${l.id}`, getAuthHeaders()); toast.success("Deleted"); fetchData(); } catch { toast.error("Failed"); }}}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-sm text-[#5C5A52]">No marketplace listings yet.</p>}
              </div>

              {/* Marketplace Opportunities */}
              <div className="bg-white border-2 border-[#D1CFC5] rounded-xl p-4">
                <h3 className="font-semibold text-[#1A1915] mb-3">Opportunities ({mpOpportunities.length})</h3>
                {mpOpportunities.length > 0 ? (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {mpOpportunities.map(o => (
                      <div key={o.id} className={`flex items-center justify-between p-3 rounded-lg border ${o.is_approved ? "border-[#D1CFC5]" : "border-[#D4A017] bg-[#D4A017]/5"}`} data-testid={`mp-opp-${o.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-[#1A1915] truncate">{o.title} <span className="text-xs text-[#5C5A52]">({o.post_type})</span></p>
                          <p className="text-xs text-[#5C5A52]">{o.town} | {o.contact_email || "No email"}</p>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                          {!o.is_approved && <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-[#2C4A3B]" onClick={async () => { try { await axios.put(`${API}/admin/marketplace/opportunities/${o.id}/approve`, {}, getAuthHeaders()); toast.success("Approved"); fetchData(); } catch { toast.error("Failed"); }}}><Check className="w-4 h-4" /></Button>}
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-[#B85C38]" onClick={async () => { try { await axios.delete(`${API}/admin/marketplace/opportunities/${o.id}`, getAuthHeaders()); toast.success("Deleted"); fetchData(); } catch { toast.error("Failed"); }}}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-sm text-[#5C5A52]">No marketplace opportunities yet.</p>}
              </div>

              {/* Construction Posts */}
              <div className="bg-white border-2 border-[#D4A017]/30 rounded-xl p-4">
                <h3 className="font-semibold text-[#1A1915] mb-3">Construction Hub ({mpConstruction.length})</h3>
                {mpConstruction.length > 0 ? (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {mpConstruction.map(c => (
                      <div key={c.id} className={`flex items-center justify-between p-3 rounded-lg border ${c.is_approved ? "border-[#D1CFC5]" : "border-[#D4A017] bg-[#D4A017]/5"}`} data-testid={`mp-const-${c.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-[#1A1915] truncate">{c.title} <span className="text-xs text-[#5C5A52]">({c.post_type})</span></p>
                          <p className="text-xs text-[#5C5A52]">{c.town}</p>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                          {!c.is_approved && <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-[#2C4A3B]" onClick={async () => { try { await axios.put(`${API}/admin/marketplace/construction/${c.id}/approve`, {}, getAuthHeaders()); toast.success("Approved"); fetchData(); } catch { toast.error("Failed"); }}}><Check className="w-4 h-4" /></Button>}
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-[#B85C38]" onClick={async () => { try { await axios.delete(`${API}/admin/marketplace/construction/${c.id}`, getAuthHeaders()); toast.success("Deleted"); fetchData(); } catch { toast.error("Failed"); }}}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-sm text-[#5C5A52]">No construction posts yet.</p>}
              </div>

              {/* Signals */}
              <div className="bg-white border-2 border-[#D1CFC5] rounded-xl p-4">
                <h3 className="font-semibold text-[#1A1915] mb-3">Signals ({mpSignals.length})</h3>
                {mpSignals.length > 0 ? (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {mpSignals.map(s => (
                      <div key={s.id} className={`flex items-center justify-between p-3 rounded-lg border ${s.is_approved ? "border-[#D1CFC5]" : "border-[#D4A017] bg-[#D4A017]/5"}`} data-testid={`mp-sig-${s.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-[#1A1915] truncate">{s.signal_type === "offer" ? "[OFFER]" : "[REQUEST]"} {s.title}</p>
                          <p className="text-xs text-[#5C5A52]">{s.category}</p>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                          {!s.is_approved && <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-[#2C4A3B]" onClick={async () => { try { await axios.put(`${API}/admin/marketplace/signals/${s.id}/approve`, {}, getAuthHeaders()); toast.success("Approved"); fetchData(); } catch { toast.error("Failed"); }}}><Check className="w-4 h-4" /></Button>}
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-[#B85C38]" onClick={async () => { try { await axios.delete(`${API}/admin/marketplace/signals/${s.id}`, getAuthHeaders()); toast.success("Deleted"); fetchData(); } catch { toast.error("Failed"); }}}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-sm text-[#5C5A52]">No signals yet.</p>}
              </div>
            </div>
          )}

          {/* Founding Members Tab */}
          {activeTab === "founding" && (
            <AdminFounding stats={stats} foundingMembers={foundingMembers} getAuthHeaders={getAuthHeaders} fetchData={fetchData} />
          )}
        </div>
      </section>

      {/* Revenue Dashboard Tab */}
      {activeTab === "revenue" && (
        <AdminRevenue revenueStats={revenueStats} commerceTransactions={commerceTransactions} getAuthHeaders={getAuthHeaders} fetchData={fetchData} />
      )}

      {/* Blog Editor Modal */}
      <BlogPostEditor
        isOpen={showBlogEditor}
        onClose={() => { setShowBlogEditor(false); setEditingPost(null); }}
        post={editingPost}
        token={getToken()}
        onSaved={fetchData}
      />
    </div>
  );
};

export default Admin;
